/**
 * Privacy & Terms page — per docs/specs/compliance.md
 *
 * Terms of Service / Privacy Policy versioning, enforcement overview,
 * session security snapshot, and acceptance log.
 *
 * @copyright 2025 Glimbr Pty Ltd
 * @version 2026-04-22-v5
 */
import { Search } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { Fragment, useMemo, useState } from 'react';
import { Chip } from '../components/chip';
import { RoleButton } from '../components/role-gate';
import { exportCsv } from '../../lib/csv';
import { useToast } from '../components/toast';
import {
    getLegalVersionDiff,
    resendLegalNotifications,
    rollbackLegalVersion,
    useAcceptanceLog,
    useComplianceKpis,
    useLegalDocs,
    useLegalVersions,
    useNotificationKpis,
    useSessionPolicy
} from '../../lib/api';
import { getHttpErrorMessage } from '../../lib/http';
import type { LegalDocSummary, LegalVersionDiff, LegalVersionRow } from '../../lib/types';
import { CompliancePublishModal } from './compliance-publish-modal';

type PublishSlug = 'tos' | 'privacy';

const enforcementTone = (mode: LegalDocSummary['enforcement_mode']) =>
    mode === 'strict_signin_block' ? 'danger' : mode === 'modal_with_grace' ? 'warning' : 'info';

const enforcementLabel = (mode: LegalDocSummary['enforcement_mode']) =>
    mode === 'strict_signin_block' ? 'Strict' : mode === 'modal_with_grace' ? 'Grace' : 'Banner';

const slugLabel = (slug: LegalDocSummary['slug']) =>
    slug === 'tos' ? 'Terms of Service' : 'Privacy Policy';

export function CompliancePage() {
    const kpis = useComplianceKpis();
    const notificationKpis = useNotificationKpis();
    const docs = useLegalDocs();
    const tosVersions = useLegalVersions('tos');
    const privacyVersions = useLegalVersions('privacy');
    const policy = useSessionPolicy();
    const log = useAcceptanceLog();
    const [search, setSearch] = useState('');
    const [publishSlug, setPublishSlug] = useState<PublishSlug | null>(null);
    const { toast } = useToast();
    const docsList = docs.data ?? [];

    const docsBySlug = useMemo(
        () => new Map((docs.data ?? []).map((doc) => [doc.slug, doc])),
        [docs.data]
    );

    const versionHistory = useMemo(
        () => ({
            tos: tosVersions.data ?? [],
            privacy: privacyVersions.data ?? []
        }),
        [tosVersions.data, privacyVersions.data]
    );

    const handleExport = () => {
        exportCsv(
            `privacy-terms-acceptance-${new Date().toISOString().slice(0, 10)}`,
            (log.data ?? []) as unknown as Record<string, unknown>[]
        );
        toast('Acceptance log exported');
    };

    return (
        <div className="stack-16">
            <header className="page-header">
                <div className="page-title-group">
                    <h1>Privacy & terms</h1>
                    <p>
                        Manage the active Terms of Service and Privacy Policy versions, then track
                        who is still behind the latest required documents.
                    </p>
                </div>
                <div className="page-actions">
                    <RoleButton
                        scope="owner_only"
                        className="btn btn-ghost"
                        onClick={() => setPublishSlug('privacy')}
                    >
                        Publish privacy
                    </RoleButton>
                    <RoleButton
                        scope="owner_only"
                        className="btn btn-primary"
                        onClick={() => setPublishSlug('tos')}
                    >
                        Publish terms
                    </RoleButton>
                </div>
            </header>

            <section className="alert-bar">
                <div className="alert" data-severity="info">
                    <span className="msg">
                        The main app enforces the accept flow. This page verifies the worker-side
                        registry, acceptance evidence, and campaign delivery metrics.
                    </span>
                    <span className="meta">verification</span>
                </div>
            </section>

            <section className="kpi-grid kpi-grid-6" aria-label="Privacy and terms metrics">
                <KpiMini
                    label="Current terms"
                    value={`${kpis.data?.current_tos ?? '—'} · ${kpis.data?.current_tos_since ?? ''}`}
                />
                <KpiMini
                    label="Current privacy"
                    value={`${kpis.data?.current_privacy ?? '—'} · ${
                        kpis.data?.current_privacy_since ?? ''
                    }`}
                />
                <KpiMini
                    label="On latest required docs"
                    value={kpis.data ? `${kpis.data.on_latest_pct.toFixed(1)}%` : '—'}
                />
                <KpiMini
                    label="Pending latest terms"
                    value={kpis.data?.pending_reaccept.toLocaleString() ?? '—'}
                    alert={!!kpis.data && kpis.data.pending_reaccept > 0}
                />
                <KpiMini
                    label="Active policies"
                    value={kpis.data?.active_policies.toString() ?? '—'}
                />
                <KpiMini
                    label="Active sessions"
                    value={policy.data?.active_sessions?.toLocaleString() ?? '—'}
                />
            </section>

            <section className="card">
                <header className="card-header">
                    <h2 className="card-title">Active documents</h2>
                </header>
                <div className="card-body tight">
                    {docsList.map((doc) => (
                        <div
                            key={doc.slug}
                            style={{
                                display: 'grid',
                                gridTemplateColumns: '120px 1fr auto auto auto',
                                gap: 12,
                                alignItems: 'center',
                                padding: '12px 16px',
                                borderBottom: '1px solid var(--border)',
                                fontSize: 12
                            }}
                        >
                            <Chip tone={doc.slug === 'tos' ? 'info' : 'purple'}>
                                {doc.slug.toUpperCase()}
                            </Chip>
                            <div>
                                <div style={{ color: 'var(--text-primary)' }}>
                                    {doc.title} · {doc.current_version}
                                </div>
                                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                                    {doc.summary_of_change}
                                </div>
                            </div>
                            <Chip tone={enforcementTone(doc.enforcement_mode)}>
                                {enforcementLabel(doc.enforcement_mode)}
                            </Chip>
                            <span className="text-muted mono">{doc.effective_at}</span>
                            <span
                                className="mono"
                                style={{
                                    color:
                                        doc.acceptance_pct < 90
                                            ? 'var(--warning)'
                                            : 'var(--success)'
                                }}
                            >
                                {doc.acceptance_pct.toFixed(1)}%
                            </span>
                            {doc.acceptance_pct < 95 && (
                                <span
                                    style={{
                                        gridColumn: 'span 5',
                                        color: 'var(--warning)',
                                        fontSize: 11
                                    }}
                                >
                                    Pending acceptance: {(100 - doc.acceptance_pct).toFixed(1)}%
                                </span>
                            )}
                        </div>
                    ))}
                </div>
            </section>

            <section className="card">
                <header className="card-header">
                    <h2 className="card-title">Acceptance gating status</h2>
                </header>
                <div className="table-wrap">
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>Document</th>
                                <th>Mode</th>
                                <th>Visibility</th>
                                <th>Estimated pending</th>
                            </tr>
                        </thead>
                        <tbody>
                            {docsList.map((doc) => (
                                <tr key={`gate-${doc.slug}`}>
                                    <td>
                                        <strong>{slugLabel(doc.slug)}</strong>
                                        <div className="sub">{doc.current_version}</div>
                                    </td>
                                    <td>
                                        <Chip tone={enforcementTone(doc.enforcement_mode)}>
                                            {enforcementLabel(doc.enforcement_mode)}
                                        </Chip>
                                    </td>
                                    <td className="text-muted">
                                        {doc.enforcement_mode === 'strict_signin_block'
                                            ? 'Signin blocked until accepted'
                                            : doc.enforcement_mode === 'modal_with_grace'
                                              ? 'Modal with grace countdown'
                                              : 'Banner only, access allowed'}
                                    </td>
                                    <td className="mono">
                                        {(100 - doc.acceptance_pct).toFixed(1)}%
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>

            <div className="grid-split">
                <VersionHistoryCard
                    slug="tos"
                    title={slugLabel('tos')}
                    subtitle={
                        docsBySlug.get('tos')
                            ? `${docsBySlug.get('tos')?.current_version} is live now`
                            : undefined
                    }
                    rows={versionHistory.tos}
                    activeDoc={docsBySlug.get('tos')}
                />
                <VersionHistoryCard
                    slug="privacy"
                    title={slugLabel('privacy')}
                    subtitle={
                        docsBySlug.get('privacy')
                            ? `${docsBySlug.get('privacy')?.current_version} is live now`
                            : undefined
                    }
                    rows={versionHistory.privacy}
                    activeDoc={docsBySlug.get('privacy')}
                />
            </div>

            <section className="card">
                <header className="card-header">
                    <h2 className="card-title">Session security policy</h2>
                </header>
                <div className="card-body">
                    {policy.data && (
                        <dl className="kv-list">
                            <dt>Idle timeout (user)</dt>
                            <dd>{policy.data.idle_timeout_user_minutes} min</dd>
                            <dt>Idle timeout (admin)</dt>
                            <dd>{policy.data.idle_timeout_admin_minutes} min</dd>
                            <dt>Max session lifetime</dt>
                            <dd>{policy.data.max_session_lifetime_days} days</dd>
                            <dt>Require 2FA (admin)</dt>
                            <dd>
                                <Chip
                                    tone={policy.data.require_2fa_for_admins ? 'success' : 'danger'}
                                >
                                    {policy.data.require_2fa_for_admins ? 'On' : 'Off'}
                                </Chip>
                            </dd>
                            <dt>Re-auth on password change</dt>
                            <dd>
                                <Chip
                                    tone={
                                        policy.data.reauth_on_password_change ? 'success' : 'muted'
                                    }
                                >
                                    {policy.data.reauth_on_password_change ? 'On' : 'Off'}
                                </Chip>
                            </dd>
                            <dt>Re-auth on new device</dt>
                            <dd>
                                <Chip tone={policy.data.reauth_on_new_device ? 'success' : 'muted'}>
                                    {policy.data.reauth_on_new_device ? 'On' : 'Off'}
                                </Chip>
                            </dd>
                            <dt>Re-auth on terms update</dt>
                            <dd>
                                <Chip tone={policy.data.reauth_on_tos_update ? 'warning' : 'muted'}>
                                    {policy.data.reauth_on_tos_update ? 'On' : 'Off'}
                                </Chip>
                            </dd>
                            <dt>Re-auth every</dt>
                            <dd>{policy.data.reauth_every_days} days</dd>
                        </dl>
                    )}
                </div>
            </section>

            <section className="card">
                <header className="card-header">
                    <h2 className="card-title">Acceptance delivery analytics</h2>
                </header>
                <div className="card-body">
                    {notificationKpis.data ? (
                        <dl className="kv-list">
                            <dt>Sent (24h)</dt>
                            <dd className="mono">
                                {notificationKpis.data.sent_24h.toLocaleString()}
                            </dd>
                            <dt>Email open rate</dt>
                            <dd className="mono">
                                {notificationKpis.data.email_open_rate.toFixed(1)}%
                            </dd>
                            <dt>Follow-ups queued</dt>
                            <dd className="mono">
                                {notificationKpis.data.followups_queued.toLocaleString()}
                            </dd>
                            <dt>Templates needing review</dt>
                            <dd className="mono">
                                {notificationKpis.data.templates_needing_review?.toLocaleString() ??
                                    '—'}
                            </dd>
                        </dl>
                    ) : (
                        <div className="empty-state">
                            <div className="title">Notification delivery analytics unavailable</div>
                        </div>
                    )}
                </div>
            </section>

            <section className="card">
                <header className="card-header">
                    <h2 className="card-title">Acceptance log</h2>
                    <button className="btn btn-sm btn-ghost" onClick={handleExport}>
                        Export CSV
                    </button>
                </header>
                <div className="card-body tight">
                    <div className="filter-bar" style={{ padding: '0 16px', margin: '0 0 4px' }}>
                        <label className="search">
                            <Search size={13} />
                            <input
                                type="search"
                                placeholder="Search user, document"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </label>
                    </div>
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>User</th>
                                <th>Document</th>
                                <th>Version</th>
                                <th>Accepted</th>
                                <th>IP</th>
                                <th>Trigger</th>
                            </tr>
                        </thead>
                        <tbody>
                            {log.data
                                ?.filter((entry) =>
                                    search.trim()
                                        ? entry.user_email
                                              .toLowerCase()
                                              .includes(search.toLowerCase()) ||
                                          entry.document.includes(search.toLowerCase())
                                        : true
                                )
                                .map((entry) => (
                                    <tr key={entry.id}>
                                        <td>{entry.user_email}</td>
                                        <td className="text-muted">
                                            {entry.document.toUpperCase()}
                                        </td>
                                        <td className="mono">{entry.version}</td>
                                        <td className="text-muted">{entry.accepted_at}</td>
                                        <td className="mono text-muted">{entry.ip}</td>
                                        <td>
                                            <Chip tone="muted">{entry.trigger}</Chip>
                                        </td>
                                    </tr>
                                ))}
                        </tbody>
                    </table>
                </div>
            </section>

            <CompliancePublishModal
                open={!!publishSlug}
                slug={publishSlug ?? 'tos'}
                activeDoc={docsBySlug.get(publishSlug ?? 'tos') ?? null}
                onClose={() => setPublishSlug(null)}
            />
        </div>
    );
}

function VersionHistoryCard({
    slug,
    title,
    subtitle,
    rows,
    activeDoc
}: {
    slug: PublishSlug;
    title: string;
    subtitle?: string;
    rows: LegalVersionRow[];
    activeDoc?: LegalDocSummary | undefined;
}) {
    const [expandedId, setExpandedId] = useState<string | null>(null);
    const [diff, setDiff] = useState<LegalVersionDiff | null>(null);
    const [actingId, setActingId] = useState<string | null>(null);
    const qc = useQueryClient();
    const { toast } = useToast();
    const rollbackCandidate = rows[1]?.version;
    const activeAcceptance = activeDoc?.acceptance_pct;

    const refreshCompliance = async () => {
        await Promise.all([
            qc.invalidateQueries({ queryKey: ['admin', 'compliance', 'kpis'] }),
            qc.invalidateQueries({ queryKey: ['admin', 'compliance', 'documents'] }),
            qc.invalidateQueries({ queryKey: ['admin', 'compliance', 'versions', slug] }),
            qc.invalidateQueries({ queryKey: ['admin', 'notifications', 'broadcasts'] }),
            qc.invalidateQueries({ queryKey: ['admin', 'users', 'list'] }),
            qc.invalidateQueries({ queryKey: ['admin', 'users', 'segments'] })
        ]);
    };

    const viewDiff = async (row: LegalVersionRow) => {
        setActingId(row.id);
        try {
            setDiff(await getLegalVersionDiff(slug, row.id));
            setExpandedId(row.id);
        } catch (error) {
            toast(getHttpErrorMessage(error, 'Failed to load legal diff'), { kind: 'danger' });
        } finally {
            setActingId(null);
        }
    };

    const rollback = async (row: LegalVersionRow) => {
        const reason = window
            .prompt(`Rollback ${title} to ${row.version}. Reason required:`)
            ?.trim();
        if (!reason) return;

        setActingId(row.id);
        try {
            await rollbackLegalVersion(slug, row.id, reason);
            await refreshCompliance();
            toast(`Rolled back ${slug.toUpperCase()} to ${row.version}`, { kind: 'success' });
        } catch (error) {
            toast(getHttpErrorMessage(error, 'Failed to rollback legal version'), {
                kind: 'danger'
            });
        } finally {
            setActingId(null);
        }
    };

    const resend = async (row: LegalVersionRow) => {
        const reason = window
            .prompt(`Queue acceptance reminder for ${row.version}. Reason:`)
            ?.trim();
        if (!reason) return;

        setActingId(row.id);
        try {
            const result = await resendLegalNotifications(slug, row.id, reason);
            await refreshCompliance();
            toast('Legal reminder queued', {
                detail: `${result.pending_count.toLocaleString()} users pending acceptance`
            });
        } catch (error) {
            toast(getHttpErrorMessage(error, 'Failed to queue legal reminder'), {
                kind: 'danger'
            });
        } finally {
            setActingId(null);
        }
    };

    return (
        <section className="card">
            <header className="card-header">
                <div>
                    <h2 className="card-title">{title}</h2>
                    {subtitle && <p className="text-muted">{subtitle}</p>}
                </div>
                <Chip tone={rollbackCandidate ? 'success' : 'warning'}>
                    {rollbackCandidate
                        ? `Rollback target: ${rollbackCandidate}`
                        : 'No prior version for rollback'}
                </Chip>
            </header>
            <p
                style={{
                    margin: '0 16px 10px',
                    color: 'var(--text-muted)',
                    fontSize: 11
                }}
            >
                {activeAcceptance === undefined
                    ? 'Acceptance gap unavailable for lifecycle view.'
                    : `Acceptance gap on live version: ${(100 - activeAcceptance).toFixed(
                          1
                      )}% · pending snapshot is advisory until user-total source stabilizes.`}
            </p>
            <div className="table-wrap">
                <table className="admin-table">
                    <thead>
                        <tr>
                            <th>Version</th>
                            <th>Published</th>
                            <th>Mode</th>
                            <th>Supersedes</th>
                            <th>Accept %</th>
                            <th>Audit comparison</th>
                            <th>Lifecycle</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, index) => {
                            const prior = rows[index + 1];
                            const supersedes = prior ? prior.version : '—';
                            const isExpanded = expandedId === row.id;

                            return (
                                <Fragment key={row.id}>
                                    <tr key={row.id}>
                                        <td className="mono">{row.version}</td>
                                        <td className="text-muted">
                                            {row.published_at}
                                            <div className="sub">{row.published_by}</div>
                                        </td>
                                        <td>
                                            <Chip tone={enforcementTone(row.enforcement_mode)}>
                                                {enforcementLabel(row.enforcement_mode)}
                                            </Chip>
                                        </td>
                                        <td className="mono">{supersedes}</td>
                                        <td className="mono">{row.acceptance_pct.toFixed(1)}%</td>
                                        <td>
                                            <button
                                                type="button"
                                                className="btn btn-xs btn-ghost"
                                                disabled={!prior}
                                                onClick={() => void viewDiff(row)}
                                            >
                                                {prior
                                                    ? isExpanded
                                                        ? 'Refresh body diff'
                                                        : 'View body diff'
                                                    : 'No prior version'}
                                            </button>
                                        </td>
                                        <td>
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    gap: 6,
                                                    flexWrap: 'wrap'
                                                }}
                                            >
                                                <RoleButton
                                                    scope="support_ok"
                                                    className="btn btn-xs btn-ghost"
                                                    disabled={actingId === row.id}
                                                    onClick={() => void resend(row)}
                                                >
                                                    Resend
                                                </RoleButton>
                                                <RoleButton
                                                    scope="owner_only"
                                                    className="btn btn-xs btn-ghost"
                                                    disabled={row.is_active || actingId === row.id}
                                                    onClick={() => void rollback(row)}
                                                >
                                                    Rollback
                                                </RoleButton>
                                            </div>
                                        </td>
                                    </tr>
                                    {isExpanded && diff ? (
                                        <tr key={`${row.id}-diff`}>
                                            <td colSpan={7}>
                                                <div
                                                    style={{
                                                        padding: 10,
                                                        marginBottom: 8,
                                                        border: '1px solid var(--border)',
                                                        borderRadius: 6,
                                                        background: 'var(--bg-input)',
                                                        fontSize: 12
                                                    }}
                                                >
                                                    <div style={{ color: 'var(--text-muted)' }}>
                                                        Body diff vs{' '}
                                                        {diff.compare_to?.version ??
                                                            'empty baseline'}
                                                    </div>
                                                    <dl
                                                        className="kv-list"
                                                        style={{ margin: '6px 0 0' }}
                                                    >
                                                        <dt>Current body chars</dt>
                                                        <dd>{diff.diff.current_chars}</dd>
                                                        <dt>Previous body chars</dt>
                                                        <dd>{diff.diff.previous_chars}</dd>
                                                        <dt>Changed line slots</dt>
                                                        <dd>{diff.diff.changed_line_slots}</dd>
                                                    </dl>
                                                    <pre
                                                        style={{
                                                            maxHeight: 180,
                                                            overflow: 'auto',
                                                            margin: '10px 0 0',
                                                            whiteSpace: 'pre-wrap',
                                                            color: 'var(--text-muted)',
                                                            fontSize: 11
                                                        }}
                                                    >
                                                        {diff.diff.preview.current ||
                                                            'No body preview available.'}
                                                    </pre>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : null}
                                </Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </section>
    );
}

function KpiMini({ label, value, alert }: { label: string; value: string; alert?: boolean }) {
    return (
        <div className="kpi-tile" data-alert={alert ? 'true' : 'false'}>
            <span className="label">{label}</span>
            <span className="value">{value}</span>
        </div>
    );
}
